import { Controller } from 'stimulus'
import throttle from 'lodash/throttle'
import { styles_list } from 'shared/assets/javascript/google_map_silver_styles'

export default class extends Controller {
  static targets = ['map']

  get apiKey() {
    return this.element.dataset.googleMapsApiKey
  }

  get hasApiKey() {
    return !!(this.apiKey && this.apiKey.trim())
  }

  get scriptAppended() {
    return !!document.querySelector('script[src*="https://maps.googleapis.com/maps/api/"]')
  }

  get mapsApiLoaded() {
    return !!(window.google && window.google.maps)
  }

  get mapScript() {
    return document.querySelector('script[src*="https://maps.googleapis.com/maps/api/"]')
  }

  get callbackName() {
    if (!this.mapsCallbackName) {
      let callbackId = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID().replace(/-/g, '')
        : `${Date.now()}${Math.random().toString(36).slice(2)}`
      this.mapsCallbackName = `buildRoxyCafeMap_${callbackId}`
    }
    return this.mapsCallbackName
  }

  get mapInView() {
    if (!this.mapTarget) return false
    let mapTop = this.mapTarget.offsetTop + 150
    let windowTop = window.scrollY
    let windowBottom = window.scrollY + window.innerHeight
    return mapTop <= windowBottom && mapTop >= windowTop
  }

  get coordinates() {
    let { lat, lng } = this.mapTarget.dataset
    if (lat == null || lng == null || lat === '' || lng === '') {
      return null
    }

    lat = parseFloat(lat)
    lng = parseFloat(lng)

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return null
    }

    return { lat, lng }
  }

  load() {
    if (!this.hasApiKey) return

    if (this.mapsApiLoaded) {
      this.buildMap()
    } else if (this.scriptAppended) {
      this.scriptLoadHandler = this.onScriptLoad.bind(this)
      this.mapScript.addEventListener('load', this.scriptLoadHandler, { once: true })
    } else {
      this.mapsCallback = this.buildMap.bind(this)
      window[this.callbackName] = this.mapsCallback
      this.appendScript(this.callbackName)
    }
  }

  onScriptLoad() {
    if (this.mapsApiLoaded) {
      this.buildMap()
    }
  }

  appendScript(callback) {
    let script = document.createElement('script')
    script.type = 'text/javascript'
    script.setAttribute('async', '')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places,geometry&callback=${callback}`
    document.body.appendChild(script)
  }

  buildMap() {
    if (!this.hasMapTarget) return
    if (!this.coordinates) return

    if (this.mapsCallbackName && window[this.mapsCallbackName] === this.mapsCallback) {
      delete window[this.mapsCallbackName]
    }

    this.map = new google.maps.Map(this.mapTarget, {
      disableDefaultUI: true,
      draggable: true,
      scrollable: false,
      center: this.coordinates,
      mapTypeControl: false,
      streetViewControl: false,
      zoom: 14.7
    })

    this.map.setOptions({ styles: styles_list })

    if (this.mapInView) {
      this.createMarker()
    } else {
      this.pinCreated = false
      this.scrollHandler = throttle(this.scroll.bind(this), 250)
      window.addEventListener('scroll', this.scrollHandler)
    }

    this.resizeHandler = throttle(this.mapZoom.bind(this), 250)
    this.mapZoom()
    window.addEventListener('resize', this.resizeHandler)
  }

  scroll() {
    if (this.mapInView && !this.pinCreated) {
      this.createMarker()
      this.pinCreated = true

      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler)
        this.scrollHandler.cancel()
      }
    }
  }

  createMarker() {
    let coordinates = this.coordinates
    if (!coordinates) return

    let markerOptions = {
      position: coordinates,
      animation: google.maps.Animation.DROP,
      title: 'Location',
      map: this.map
    }

    if (this.mapTarget.dataset.icon) {
      let iconOptions = { url: this.mapTarget.dataset.icon }
      if (this.mapTarget.dataset.iconWidth && this.mapTarget.dataset.iconHeight) {
        iconOptions.scaledSize = new google.maps.Size(
          parseInt(this.mapTarget.dataset.iconWidth, 10),
          parseInt(this.mapTarget.dataset.iconHeight, 10)
        )
      }
      markerOptions.icon = iconOptions
    }

    new google.maps.Marker(markerOptions)
    this.map.setCenter(new google.maps.LatLng(coordinates.lat, coordinates.lng))
  }

  mapZoom() {
    let width = window.innerWidth
    if (width < 1280 && width > 699) {
      this.map.setZoom(14)
    } else if (width < 700) {
      this.map.setZoom(13.2)
    } else {
      this.map.setZoom(14.7)
    }
  }

  disconnect() {
    if (this.mapsCallbackName && window[this.mapsCallbackName] === this.mapsCallback) {
      delete window[this.mapsCallbackName]
    }
    if (this.scriptLoadHandler && this.mapScript) {
      this.mapScript.removeEventListener('load', this.scriptLoadHandler)
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler.cancel()
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
      this.resizeHandler.cancel()
    }
  }
}
